// This pragma is required for the css utility to work
/** @jsx jsx */
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { GreenSpinner, WhiteSpinner } from '~/components/Spinner';
import { HeightSize } from '~/constants/form';
import * as React from 'react';
import { colors, fontFamily, fontWeight } from '~/styles/constants';

const Color = require('color');

type ButtonSize = 'medium' | 'large' | 'mediumLarge';

interface Dimensions {
  width?: number;
  height?: number;
}

const buttonSizeDimensions: { [key in ButtonSize]: Dimensions } = {
  large: {
    height: HeightSize.large,
  },
  mediumLarge: {
    height: HeightSize.mediumLarge,
  },
  medium: {
    height: HeightSize.medium,
  },
};

const baseButton = css`
  font-family: ${fontFamily};
  position: relative;
  display: inline-block;
  text-align: center;
  cursor: pointer;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  outline: none;
  border-radius: 3px;

  line-height: 20px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: ${fontWeight.medium};

  align-items: center;

  :disabled {
    cursor: not-allowed;
  }
`;

const LoadingText = styled('span')`
  margin-right: 10px;
`;

interface StyledButtonProps {
  fullWidth?: boolean;
  dimensions?: Dimensions;
  rounded?: boolean;
  extraRounded?: boolean;
}

export const StyledButton = styled('button')<StyledButtonProps>`
  ${baseButton};

  ${(props) =>
    props.dimensions && props.dimensions.width ? `width: ${props.dimensions.width}px;` : ''}

  ${(props) =>
    props.dimensions && props.dimensions.height ? `height: ${props.dimensions.height}px;` : ''}

  ${(props) => (props.fullWidth ? 'width: 100%;' : '')};
`;

export interface ButtonProps {
  className?: string;
  disabled?: boolean;
  ref?: React.RefObject<HTMLButtonElement>;
  /**
   * Called when Button is clicked
   */
  onClick?(event: React.MouseEvent<HTMLElement>): void;
  type?: 'button' | 'submit' | 'reset';
  isLoading?: boolean;
  loadingText?: string;
  useGreenSpinner?: boolean;
  tabIndex?: number;
  id?: string;
  dataPendo?: string;
  dataE2E?: string;
  dataTestid?: string;
  fullWidth?: boolean;
  size?: ButtonSize;
  rounded?: boolean;
  extraRounded?: boolean;
  css?: SerializedStyles;
  form?: string;
  dataAnalytics?: string; // heap event identifier
  children?: React.ReactNode;
}

const Button = (props: ButtonProps) => {
  const {
    disabled,
    onClick,
    type,
    tabIndex,
    id,
    dataPendo,
    dataAnalytics,
    dataE2E,
    dataTestid,
    size,
    fullWidth,
    rounded,
    extraRounded,
    loadingText,
    css,
    form,
  } = props;

  const buttonProps = {
    disabled,
    onClick,
    type,
    tabIndex,
    id,
    css,
    form,
  };

  const Spinner = props.useGreenSpinner ? GreenSpinner : WhiteSpinner;
  const dimensions = size ? buttonSizeDimensions[size] : buttonSizeDimensions.medium;

  return (
    <StyledButton
      {...buttonProps}
      className={props.className}
      data-pendo={dataPendo}
      data-heap-event={dataAnalytics}
      data-analytics={dataAnalytics}
      data-e2e={dataE2E}
      data-testid={dataTestid}
      dimensions={dimensions}
      fullWidth={fullWidth}
      rounded={rounded}
      extraRounded={extraRounded}
    >
      {props.isLoading ? (
        <React.Fragment>
          {loadingText && <LoadingText>{loadingText}</LoadingText>}
          <Spinner size={20} />
        </React.Fragment>
      ) : (
        props.children
      )}
    </StyledButton>
  );
};

Button.defaultProps = {
  disabled: false,
  type: 'button',
} as Partial<ButtonProps>;

export default Button;

const disabledPrimaryColor = Color(colors.primary).lighten(0.4).hex();

export const primaryCss = css`
  color: white;
  background-color: ${colors.primary};
  border: none;
  :hover,
  :focus {
    background-color: ${colors.primaryFocus};
    box-shadow:
      0px 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 4px 5px rgba(0, 0, 0, 0.14),
      0px 1px 10px rgba(0, 0, 0, 0.12);
  }

  :disabled {
    background-color: ${colors.forest700};
    color: ${colors.white};
    font-weight: normal;
    box-shadow: none;
    opacity: 0.5;
  }
`;

export const disabledButtonCSS = css`
  color: white;
  border: none !important;
  background-color: ${disabledPrimaryColor} !important;
  cursor: not-allowed;
  outline: 0 !important;

  :focus {
    box-shadow: none !important;
    border: 0 !important;
  }
`;

export const PrimaryButton = styled(Button)`
  ${primaryCss};
`;

const darkerPrimaryAlternative = Color(colors.primaryAlternative).darken(0.1).hex();
const disabledPrimaryAlternative = Color(colors.primaryAlternative).lighten(0.4).hex();

export const primaryAlternativeCss = css`
  color: ${colors.primary};
  background-color: transparent;
  border: none;
  font-weight: ${fontWeight.medium};

  :hover,
  :focus {
    background-color: ${colors.forest100};
  }

  :disabled {
    color: ${colors.gray700};
    background-color: ${colors.forest50};
    font-weight: normal;
  }
`;

export const PrimaryAlternativeButton = styled(Button)`
  ${primaryAlternativeCss};
`;

const dangerAlternativeCss = css`
  color: ${colors.ruby700};
  background-color: white;
  border: none;

  :hover,
  :focus {
    color: ${colors.ruby700};
    background-color: ${colors.ruby50};
    box-shadow:
      0px 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 4px 5px rgba(0, 0, 0, 0.14),
      0px 1px 10px rgba(0, 0, 0, 0.12);
  }

  :disabled {
    color: ${colors.white};
    background-color: ${Color(colors.ruby700).alpha(0.5)};
  }
`;

export const DangerAlternativeButton = styled(Button)`
  ${dangerAlternativeCss};
`;

export const greyButton = css`
  color: ${colors.secondary};
  background-color: transparent;
  border: 1px solid ${colors.secondary};
  font-weight: normal;
`;

export const lightGrayButton = greyButton;

export const GreyPrimaryAlternativeButton = styled(Button)`
  ${greyButton};

  :hover,
  :focus {
    color: ${colors.secondary};
    border-color: ${colors.secondary};
    background-color: ${colors.gray100};
  }

  :disabled {
    color: ${colors.secondary};
    border-color: transparent;
    background-color: ${colors.gray200};
    box-shadow: none;
  }
`;
const hoverLightGrayPrimaryAlternativeButton = Color(colors.gray400).darken(0.1).hex();
export const LightGrayPrimaryAlternativeButton = styled(Button)`
  background-color: ${colors.gray400};
  color: ${colors.white};
  border: 1px solid ${colors.gray400};

  :hover {
    background-color: ${hoverLightGrayPrimaryAlternativeButton};
    border-color: ${hoverLightGrayPrimaryAlternativeButton};
  }
`;

export const SmallPrimaryButton = styled(PrimaryButton)`
  font-size: 13px;
  font-weight: 400;
  height: 37px;
`;

export const DangerButton = styled(Button)`
  color: ${colors.dangerRed};
  background-color: white;
  border: none;

  :hover,
  :focus {
    color: ${colors.white};
    background-color: ${colors.ruby700};
    box-shadow:
      0px 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 4px 5px rgba(0, 0, 0, 0.14),
      0px 1px 10px rgba(0, 0, 0, 0.12);
  }

  :disabled {
    opacity: 0.5;
    -webkit-opacity: 0.5;
    box-shadow: none;
  }
`;

export const SecondaryButton = styled(Button)`
  color: ${colors.secondary};
  border: none;
  background-color: transparent;
`;

export const primaryAlternativePaintedCss = css`
  color: white;
  background-color: ${colors.primaryAlternative};
  border: 1px solid ${colors.primaryAlternative};

  :hover {
    background-color: ${darkerPrimaryAlternative};
    border-color: ${darkerPrimaryAlternative};
  }

  :disabled {
    background-color: ${disabledPrimaryAlternative};
    border-color: ${disabledPrimaryAlternative};
    box-shadow: none;
  }

  :focus {
    box-shadow: 0 0 0 1px ${colors.primaryAlternative};
  }
`;

export const AlternativeButton = styled(Button)`
  ${primaryAlternativePaintedCss};
`;

export const PrimaryBlueButton = styled(PrimaryButton)`
  ${primaryAlternativePaintedCss};
`;

export const primaryGreyPaintedCss = css`
  color: ${colors.gray800};
  background-color: transparent;
  border: 1px solid transparent;
  box-sizing: border-box;
  outline: none;

  :hover,
  :focus {
    background-color: ${colors.gray100};
    border-color: ${colors.gray100};
  }

  :disabled {
    color: ${colors.gray800};
    opacity: 0.5;
  }
`;

export const PrimaryGreyButton = styled(Button)`
  ${primaryGreyPaintedCss}
`;
